import { DataOwner, DEFAULT_DATA_OWNER } from 'serviceWorker/db/dataOwner';
import { subscribeSwLifecycleAbort } from 'serviceWorker/helpers/swLifecycleAbort';
import type { AnyChannelContract } from 'shared/channels/contract';
import { markPullRunning } from './runningPulls';

/**
 * Creates an in-memory guard to prevent parallel pulls within a single SW session.
 *
 * Why in-memory instead of checking `isDownloading` from DB:
 * - Persisted `isDownloading: true` would block recovery from stuck state after SW restart
 * - In-memory flag resets when SW is killed, allowing stuck state recovery
 * - Within a live session, it still prevents parallel pulls (race condition)
 *
 * Abort on logout/clear is subscribed via {@link subscribeSwLifecycleAbort}
 * (listeners on `self` are registered once at SW evaluation).
 *
 * @example
 * const pullGuard = createPullGuard();
 *
 * // Simple usage with execute():
 * if (shouldPull) {
 *   await pullGuard.execute(async (abortController) => {
 *     await entityService.reset();
 *     await entityService.add({ isDownloading: true, isDownloaded: false });
 *     await pullEntities(setup, abortController);
 *   });
 * }
 */
export function createPullGuard(contract?: AnyChannelContract, { owner = DEFAULT_DATA_OWNER }: { owner?: DataOwner } = {}) {
  let isPulling = false;
  let currentAbortController: AbortController | null = null;
  let unsubscribeAbort: (() => void) | null = null;
  let markPullEnded: (() => void) | null = null;

  /**
   * `contract`: the entity's, so the worker can tell a running download from a stale flag.
   * `owner`: a project switch does not abort a download of data of the user (projects, organizations).
   */
  const beginPull = (): void => {
    isPulling = true;
    markPullEnded = contract ? markPullRunning(contract) : null;
  };

  const endPull = (abortController = currentAbortController): void => {
    if (abortController && currentAbortController !== abortController) {
      return;
    }

    isPulling = false;
    markPullEnded?.();
    markPullEnded = null;
    currentAbortController = null;
    if (unsubscribeAbort) {
      unsubscribeAbort();
      unsubscribeAbort = null;
    }
  };

  const abort = (): void => {
    currentAbortController?.abort();
    endPull();
  };

  return {
    /** Check if a pull is currently in progress */
    get isPulling(): boolean {
      return isPulling;
    },

    /**
     * Attempt to start a guarded pull operation.
     * Returns true if pull can proceed, false if already pulling.
     * If true is returned, caller MUST call endPull() when done.
     */
    startPull(): boolean {
      if (isPulling) return false;
      beginPull();
      return true;
    },

    /** Mark pull as complete. */
    endPull,

    /**
     * Execute a pull operation with automatic guard management and abort handling.
     * - Skips if already pulling
     * - Subscribes to the loggedOut / permissionsChanged / projectDataReplaced abort
     * - Automatically releases guard on completion or abort
     *
     * @param pullFn - Function that performs the pull, receives AbortController
     * @param onAbort - Optional callback when pull is aborted
     * @returns Promise that resolves when pull completes (or immediately if skipped)
     */
    async execute(
      pullFn: (abortController: AbortController) => Promise<void>,
      onAbort?: () => void
    ): Promise<void> {
      if (isPulling) return;
      beginPull();

      const abortController = new AbortController();
      currentAbortController = abortController;

      const abortHandler = (): void => {
        onAbort?.();
        abort();
      };

      unsubscribeAbort = subscribeSwLifecycleAbort(abortHandler, { includeProjectSwitch: owner === DataOwner.PROJECT });

      return pullFn(abortController).finally(() => endPull(abortController));
    },
  };
}

export type PullGuard = ReturnType<typeof createPullGuard>;
